import Decimal from "decimal.js";

export const round = (number: number, decimalPlaces: number) =>
  Math.round((number + Number.EPSILON) * Math.pow(10, decimalPlaces)) /
  Math.pow(10, decimalPlaces);

export const roundDown = (number: number, decimalPlaces: number): number =>
  Math.floor(number * Math.pow(10, decimalPlaces)) /
  Math.pow(10, decimalPlaces);

export interface FormatNumberOptions {
  percent?: boolean;
  prefix?: string;
  suffix?: string;
  currency?: string; // e.g., "NOK", "USD"
  locale?: string; // e.g., "no-NO", "en-US"
  notation?: "standard" | "compact" | "scientific" | "engineering";
  signDisplay?: "auto" | "never" | "always";
  useGrouping?: boolean; // Show thousand separators
  compact?: boolean; // if true, format large numbers as 10k, 3M, 2B etc when >= 10_000
}

export const roundNumber = (
  number: number | null,
  decimalPlaces: number
): number => {
  return number !== null
    ? new Decimal(number).toDecimalPlaces(decimalPlaces).toNumber()
    : 0;
};

export const formatNumber = (
  number: number,
  decimalPlaces = 0,
  options: FormatNumberOptions = {}
): string => {
  const {
    percent = false,
    prefix = "",
    suffix = "",
    currency,
    locale = "no-NO",
    notation = "standard",
    signDisplay = "auto",
    useGrouping = true,
    compact = false,
  } = options;

  let value = number;

  // Convert to percentage if needed
  if (percent) {
    value = number * 100;
  }

  const roundedNumber = round(value, decimalPlaces);

  // Compact formatting: 10k, 3 mill, 1,5 mrd (applies only when requested and value >= 10_000)
  if (compact && Math.abs(value) >= 10_000) {
    const abs = Math.abs(value);
    const units = [
      // { v: 1_000_000_000, suffix: " mrd" },
      { v: 1_000_000, suffix: " mill" },
      { v: 1_000, suffix: "k" },
    ];

    for (const u of units) {
      if (abs >= u.v) {
        const scaled = value / u.v;
        const roundedScaled = round(scaled, 1); // Always use 1 decimal for compact
        // Format with 1 decimal but remove trailing zeros
        const scaledStr = roundedScaled.toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
        let result = `${scaledStr}${u.suffix}`;
        if (prefix) result = prefix + result;
        if (suffix) result = result + suffix;
        if (percent && !suffix && !currency) result = result + "%";
        return result;
      }
    }
  }

  // Build Intl.NumberFormat options
  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    notation,
    signDisplay,
    useGrouping,
  };

  // Add currency if specified
  if (currency) {
    formatOptions.style = "currency";
    formatOptions.currency = currency;
  }

  const formatted = roundedNumber.toLocaleString(locale, formatOptions);

  // Apply prefix and suffix
  let result = formatted;
  if (prefix) result = prefix + result;
  if (suffix) result = result + suffix;
  if (percent && !suffix && !currency) result = result + " %";

  return result;
};
